/**
 * SectionNode Component
 *
 * A container node for grouping related workflow nodes.
 * Can be collapsed/expanded to hide/show contained nodes.
 * Useful for organizing complex workflows into logical sections.
 */

import { memo, useState, useCallback } from 'react';
import { type NodeProps, type Node, NodeResizer } from '@xyflow/react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
  Input,
  shorthands,
} from '@fluentui/react-components';
import {
  ChevronDownRegular,
  ChevronRightRegular,
  EditRegular,
  DeleteRegular,
  CheckmarkRegular,
  DismissRegular,
} from '@fluentui/react-icons';

export type SectionNodeData = {
  /** Section label/title */
  label: string;
  /** Whether the section is collapsed */
  isCollapsed?: boolean;
  /** Background color (optional) */
  color?: 'neutral' | 'brand' | 'green' | 'purple' | 'berry';
  /** Description text */
  description?: string;
  /** Callback when collapse state changes */
  onCollapseChange?: (sectionId: string, collapsed: boolean) => void;
  /** Callback when label changes */
  onLabelChange?: (sectionId: string, label: string) => void;
  /** Callback when section is deleted */
  onDelete?: (sectionId: string) => void;
};

export type SectionNodeType = Node<SectionNodeData, 'section'>;

const useStyles = makeStyles({
  container: {
    width: '100%',
    height: '100%',
    minWidth: '200px',
    minHeight: '150px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke2),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  containerSelected: {
    ...shorthands.borderColor(tokens.colorBrandStroke1),
    ...shorthands.borderStyle('solid'),
  },
  containerCollapsed: {
    minHeight: 'auto',
    height: 'auto !important',
  },
  // Color variants
  colorBrand: {
    backgroundColor: tokens.colorBrandBackground2,
    ...shorthands.borderColor(tokens.colorBrandStroke2),
  },
  colorGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    ...shorthands.borderColor(tokens.colorPaletteGreenBorder1),
  },
  colorPurple: {
    backgroundColor: tokens.colorPalettePurpleBackground2,
    ...shorthands.borderColor(tokens.colorPalettePurpleForeground2),
  },
  colorBerry: {
    backgroundColor: tokens.colorPaletteBerryBackground2,
    ...shorthands.borderColor(tokens.colorPaletteBerryBorder2),
  },
  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    cursor: 'move', // Indicate draggable
  },
  collapseButton: {
    minWidth: 'auto',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  labelContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  labelInput: {
    flex: 1,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: '0.15s',
  },
  actionsVisible: {
    opacity: 1,
  },
  actionButton: {
    minWidth: 'auto',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  // Content area (where child nodes go)
  content: {
    flex: 1,
    position: 'relative',
    padding: tokens.spacingVerticalM,
  },
  contentCollapsed: {
    display: 'none',
  },
  // Description
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    padding: `0 ${tokens.spacingHorizontalM} ${tokens.spacingVerticalS}`,
  },
  // Node count badge
  countBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusCircular,
  },
});

/**
 * SectionNode - Container for grouping workflow nodes
 */
export const SectionNode = memo(({ id, data, selected }: NodeProps<SectionNodeType>) => {
  const styles = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(data.label);
  const [isHovered, setIsHovered] = useState(false);

  const isCollapsed = data.isCollapsed ?? false;

  const handleCollapseToggle = useCallback(() => {
    data.onCollapseChange?.(id, !isCollapsed);
  }, [id, isCollapsed, data]);

  const handleEditStart = useCallback(() => {
    setEditValue(data.label);
    setIsEditing(true);
  }, [data.label]);

  const handleEditSave = useCallback(() => {
    if (editValue.trim()) {
      data.onLabelChange?.(id, editValue.trim());
    }
    setIsEditing(false);
  }, [id, editValue, data]);

  const handleEditCancel = useCallback(() => {
    setEditValue(data.label);
    setIsEditing(false);
  }, [data.label]);

  const handleEditKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  }, [handleEditSave, handleEditCancel]);

  const handleDelete = useCallback(() => {
    data.onDelete?.(id);
  }, [id, data]);

  const getColorClass = () => {
    switch (data.color) {
      case 'brand':
        return styles.colorBrand;
      case 'green':
        return styles.colorGreen;
      case 'purple':
        return styles.colorPurple;
      case 'berry':
        return styles.colorBerry;
      default:
        return undefined;
    }
  };

  return (
    <>
      {/* Resizer for adjusting section size */}
      <NodeResizer
        minWidth={200}
        minHeight={isCollapsed ? 50 : 150}
        isVisible={selected}
        lineClassName="nodrag"
        handleClassName="nodrag"
      />

      <div
        className={mergeClasses(
          styles.container,
          selected && styles.containerSelected,
          isCollapsed && styles.containerCollapsed,
          getColorClass()
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Header with collapse toggle and label */}
        <div className={styles.header}>
          <Button
            className={styles.collapseButton}
            appearance="subtle"
            icon={isCollapsed ? <ChevronRightRegular /> : <ChevronDownRegular />}
            onClick={handleCollapseToggle}
            aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
            aria-expanded={!isCollapsed}
          />

          <div className={styles.labelContainer}>
            {isEditing ? (
              <>
                <Input
                  className={styles.labelInput}
                  size="small"
                  value={editValue}
                  onChange={(_, inputData) => setEditValue(inputData.value)}
                  onKeyDown={handleEditKeyDown}
                  autoFocus
                />
                <Button
                  className={styles.actionButton}
                  appearance="subtle"
                  icon={<CheckmarkRegular />}
                  onClick={handleEditSave}
                  aria-label="Save"
                />
                <Button
                  className={styles.actionButton}
                  appearance="subtle"
                  icon={<DismissRegular />}
                  onClick={handleEditCancel}
                  aria-label="Cancel"
                />
              </>
            ) : (
              <Text className={styles.label}>{data.label}</Text>
            )}
          </div>

          {/* Action buttons - visible on hover */}
          {!isEditing && (
            <div className={mergeClasses(styles.actions, (isHovered || selected) && styles.actionsVisible)}>
              <Button
                className={styles.actionButton}
                appearance="subtle"
                icon={<EditRegular />}
                onClick={handleEditStart}
                aria-label="Edit label"
              />
              <Button
                className={styles.actionButton}
                appearance="subtle"
                icon={<DeleteRegular />}
                onClick={handleDelete}
                aria-label="Delete section"
              />
            </div>
          )}
        </div>

        {/* Description (if provided) */}
        {data.description && !isCollapsed && (
          <Text className={styles.description}>{data.description}</Text>
        )}

        {/* Content area - child nodes are positioned here via ReactFlow's parentId */}
        <div className={mergeClasses(styles.content, isCollapsed && styles.contentCollapsed)}>
          {/* Child nodes are rendered by ReactFlow, not directly here */}
        </div>
      </div>
    </>
  );
});

SectionNode.displayName = 'SectionNode';
